import React, { type FC } from 'react';
import { Modal, Select } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { Form, InputNumber } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { SystemUser } from 'types';

export namespace AddDiscountModal {
  export type Props = {
    isOpen: boolean;
    onCancel: () => void;
    onOk: (discount: { type: number; approver: number; amount: number; reason: number }) => void;
    discountReasons: { reason_desc: string; ID: number }[];
    discountTypeTemplates: DefaultOptionType;
    discountApprovers: SystemUser[] | undefined;
    isLoading: boolean;
  };

  export const $: FC<Props> = observer((props) => {
    const { isOpen, onCancel, onOk, discountReasons, discountTypeTemplates, discountApprovers, isLoading } = props;
    const [form] = Form.useForm();

    return (
      <Modal.$
        open={isOpen}
        title="Add Discount"
        okText="Save"
        okButtonProps={{ disabled: isLoading }}
        onOk={form.submit}
        onCancel={onCancel}
        dataTestId="add-discount-modal"
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          onFinish={(values) => {
            onOk({
              type: values.type,
              reason: values.reason,
              approver: values.approver,
              amount: values.amount,
            });
          }}
        >
          <Form.Item
            name="type"
            label="Discount Type"
            rules={[{ required: true, message: 'Please select a discount type!' }]}
          >
            <Select.$
              name="discount-type-template"
              placeholder="Please select"
              dataTestId="discount-type"
              options={discountTypeTemplates?.map((option: { discount_desc: string; ID: number }) => ({
                label: option.discount_desc,
                value: option.ID,
              }))}
            />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Please select a reason!' }]}>
            <Select.$
              name="discount-reason"
              placeholder="Please select"
              dataTestId="discount-reason"
              options={discountReasons?.map((option: { reason_desc: string; ID: number }) => ({
                label: option.reason_desc,
                value: option.ID,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="approver"
            label="Approved By"
            rules={[{ required: true, message: 'Please select an approver!' }]}
          >
            <Select.$
              name="discount-approver"
              placeholder="Please select"
              dataTestId="discount-approver"
              options={discountApprovers?.map((option: { name: string; ID: number }) => ({
                label: option.name,
                value: option.ID,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: 'Please enter an amount!' },
              {
                validator: (_, value) => {
                  if (value > 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Amount must be greater than 0!'));
                },
              },
            ]}
          >
            <InputNumber placeholder="0" min={0} />
          </Form.Item>
        </Form>
      </Modal.$>
    );
  });
}
